import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { Preferences } from '@capacitor/preferences'
import { SplashScreen } from '@capacitor/splash-screen'
import { init } from '@sentry/capacitor'
import { History } from 'history'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { initIntercom } from '../plugins/Intercom'
import { setSession } from '../redux/sessionSlice'
import request from '../request'
import { selectCustomer, selectSession } from '../store'
import { debounce } from '../utils'

const whiteListedLandingRoutes = ['/qiot', '/qiot/test']

const themeColorMapping: { [key: string]: string } = {
  smartservice: '#000',
}

export default function useAppStart(history: History) {
  const { theme, authToken, locale, externalRefId, phoneCharacteristics } = useSelector(selectSession)
  const customer = useSelector(selectCustomer)
  const dispatch = useDispatch()

  useEffect(() => {
    if (theme) document.body.setAttribute('color-theme', theme)
    if (themeColorMapping[theme]) document.getElementsByName('theme-color')[0].setAttribute('content', '#000')
  }, [theme])

  const localStorageFetch = debounce(async () => {
    const { keys } = await Preferences.keys()

    const loadedItems = await Promise.all(
      keys.map(async (key) => {
        const { value } = await Preferences.get({ key })

        return { key, value: JSON.parse(value || '""') }
      })
    )

    const availableLocales = ['en', 'sv']

    const cleanedLocale = window.navigator.language.split('-')[0]

    let defaultSession = {
      locale: availableLocales.includes(cleanedLocale) ? cleanedLocale : 'en',
      phoneCharacteristics: {
        phoneModel: 'web',
        appVersion: 'web',
      },
    }

    if (Capacitor.isNativePlatform()) {
      const [{ model }, { version }] = await Promise.all([Device.getInfo(), App.getInfo()])

      defaultSession = {
        ...defaultSession,
        phoneCharacteristics: {
          phoneModel: model,
          appVersion: version,
        },
      }
    }

    if (loadedItems.length < 2) {
      const savedLocale = loadedItems.find((i) => i.key === 'locale')

      dispatch(
        setSession({
          userType: '',
          authToken: '',
          externalPayloadToken: '',
          externalConsumer: '',
          externalRefId: '',
          ...defaultSession,
          locale: savedLocale?.value || defaultSession.locale,
        })
      )

      const foundWhiteListedLandingRoute = whiteListedLandingRoutes.find((route) => history.location.pathname.includes(route))

      if (!foundWhiteListedLandingRoute) {
        history.replace('/login')
      }
    } else {
      const loadedSession = loadedItems.reduce(
        (acc, { key, value }) => ({
          ...acc,
          [key]: value,
        }),
        defaultSession
      )

      dispatch(setSession(loadedSession))
    }
  }, 10000)

  useEffect(() => {
    if (typeof authToken !== 'string') {
      localStorageFetch()
    }
  }, [authToken])

  const externalRefIdLogin = debounce(async () => {
    if (!authToken && externalRefId) {
      request(`/driver/delivery_ref/${externalRefId}`).then((res) => {
        dispatch(setSession({ authToken: res.token, userType: 'driver' }))
      })
    }
  }, 10000)

  useEffect(() => {
    if (externalRefId?.length) {
      externalRefIdLogin()
    }
  }, [externalRefId])

  const intercomFetch = debounce(initIntercom, 1000)

  useEffect(() => {
    if (customer?.customer?.email) {
      intercomFetch(customer, phoneCharacteristics, locale)
    }
  }, [customer])

  const sentryAndSplashAndStatusBar = debounce(() => {
    if (process.env.NODE_ENV === 'production') {
      init({
        dsn: 'https://98625b4291774d6fa5da9ad32d55b9f3@o458314.ingest.sentry.io/4504326969163776',
        release: 'com.qlocx.customerdev@temp',
        dist: 'temp',
      })
    }

    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide()
        .then(() => console.log('SplashScreen.hide() success'))
        .catch((err) => console.log('SplashScreen.hide() error', err))
    }
  }, 1000)
  useEffect(() => {
    sentryAndSplashAndStatusBar()
  }, [])
}
